import { Agent } from "../interfaces/agent.interface";
import { currencyDisplay, formatDate, parseCurrencyString, summarizeFare, toTitleCase } from "../utils/helpers";
import { BookingOrder, BookingSummary, BookingTransaction } from "../interfaces/booking.interface";
import { PromotionCalculatePayload, PromotionCalculationResponse, PromotionSummary } from "../interfaces/promotion.interface";
import dayjs from 'dayjs';
import axiosInstance from "../utils/axiosInstance";
import { departureDateFormat } from "../utils/antd.props";

interface PagedBookings {
  bookings: BookingSummary[],
  total: number
}

const mapNominationData = (nomination: any) => {
  const nominationData = nomination?.collection || nomination || [];
  return Array.from({ length: 3 }, (_, i) => ({
    nominee_surname: nominationData[i]?.nominee_surname || '',
    nominee_given_name: nominationData[i]?.nominee_given_name || '',
    nominee_passport_number: nominationData[i]?.nominee_passport_number || '',
    nominee_date_of_birth: nominationData[i]?.nominee_date_of_birth ? dayjs(nominationData[i].nominee_date_of_birth) : null,
    nominee_relationship: nominationData[i]?.nominee_relationship || '',
    nominee_share: nominationData[i]?.nominee_share || '',
  }));
};


class B2C2BApi {

  getAgents(itineraryId:number): Promise<Agent[]> {
    const apiUrl = `/b2c2b/api/v1/itineraries/${itineraryId}/eligible_agents`;

    return axiosInstance.get(apiUrl)
    .then(response => {
      const agents = response.data.data?.map(agentData => {
        const agent:Agent = {
          id: agentData.id,
          agency_id: agentData.attributes.agency_id,
          username: agentData.attributes.username,
          name: agentData.attributes.full_name,
          email: agentData.attributes.emails,
          image: agentData.attributes.image,
          phone: agentData.attributes.phones,
          whatsapp: agentData.attributes.whatsapp_no,
          address: agentData.attributes.address,
          city: toTitleCase(agentData.attributes.city),
          state: toTitleCase(agentData.attributes.state),
          agency_full_name: agentData.attributes.agency_full_name
        }
        return agent;
      })
      return Promise.resolve(agents);
    });
  }

  getAllPaymentMethods(){
    const apiUrl = `/b2c2b/api/v1/payments/payment_method`;

    return axiosInstance.get(apiUrl)
    .then((response:any) => {
      return Promise.resolve(response.data);
    });
  }

  getPromotions(tourId: number): Promise<PromotionSummary[]> {
    const apiUrl = `/b2c2b/api/v1/promotions`;

    return axiosInstance.get(apiUrl, { params: { tour_id: tourId } })
    .then((response:any) => {
      return Promise.resolve(response.data?.promotions ?? []);
    });
  }

  calculatePromotions(payload: PromotionCalculatePayload): Promise<PromotionCalculationResponse> {
    const apiUrl = `/b2c2b/api/v1/promotions/calculate`;

    return axiosInstance.post(apiUrl, payload)
    .then((response:any) => {
      return Promise.resolve(response.data);
    });
  }

  saveBooking(payload){
    const apiUrl = `/b2c2b/api/v1/bookings`;

    return axiosInstance.post(apiUrl, payload)
    .then(response => {
      return Promise.resolve(response.data);
    });
  }

  getCustomerReferralBinding(email: string){
    const apiUrl = `/b2c2b/api/v1/customer_referral_binding`;

    return axiosInstance.get(apiUrl, { params: { email } })
    .then(response => {
      return response.data;
    });
  }

  createStickyAgentChangeRequest(payload){
    const apiUrl = `/b2c2b/api/v1/sticky_agent_change_requests`;

    return axiosInstance.post(apiUrl, payload)
    .then(response => {
      return response.data;
    });
  }

  cancelStickyAgentChangeRequest(id: number | string){
    const cancelUrl = `/b2c2b/api/v1/sticky_agent_change_requests/${id}/cancel`;
    const deleteUrl = `/b2c2b/api/v1/sticky_agent_change_requests/${id}`;

    return axiosInstance.post(cancelUrl)
      .catch(error => {
        if (error.response?.status === 404 || error.response?.status === 405) {
          return axiosInstance.delete(deleteUrl);
        }

        return Promise.reject(error);
      })
      .then(response => {
        return response.data;
      });
  }

  getAgentChangeTrackers(){
    const apiUrl = `/b2c2b/api/v1/agent_change_trackers`;

    return axiosInstance.get(apiUrl)
    .then(response => {
      return response.data;
    });
  }


  trackClick(refLink: string, keyword){
    const apiUrl = "/b2c2b/api/v1/track_click";
    const payload = {ref: refLink, keyword}
    return axiosInstance.post(apiUrl, payload)
    .then(response => {
      return Promise.resolve(response.data);
    });
  }

  validateAgentReferralLink(refLink: string, keyword){
    const queryParams = new URLSearchParams({ ref: refLink || "" });

    if(keyword){
      queryParams.set("keyword", keyword);
    }

    const apiUrl = `/b2c2b/api/v1/referral_links/validate?${queryParams.toString()}`;
    
    return axiosInstance.get(apiUrl)
    .then(response => {
      return Promise.resolve(response.data);
    });
  }


  getBookings(limit:number, currentPage: number): Promise<PagedBookings>{
    const apiUrl = `/b2c2b/api/v1/bookings?page=${currentPage}`;

    return axiosInstance.get(apiUrl)
    .then((response:any) => {
      const bookings:BookingSummary[] = response.data.data.map(bookingData => {
        const booking:BookingSummary = {
          id: bookingData.attributes.id,
          bookingDate: bookingData.attributes.created_at,
          refNo: bookingData.attributes.ref_no,
          name: bookingData.attributes.agent_name,
          bookedBy: bookingData.attributes.booked_by,
          tourCode: bookingData.attributes.code,
          pax: bookingData.attributes.total_quantity,
          destination: bookingData.attributes.destination,
          packageName: bookingData.attributes.caption,
          fileUrlCopy: bookingData.attributes.file_copy_url,
          fileCNUrlCopy: bookingData.attributes.file_cn_copy_url,
          bookingFormEnabled: bookingData.attributes.booking_form_enabled,
          bookingFormUrl: bookingData.attributes.pdf_file_url,
          status: bookingData.attributes.payment_status,
          guaranteedIndicator: bookingData.attributes.guaranteed_indicator,
          payableText: bookingData.attributes.payable_text,
        };
        return booking;
      })
      return Promise.resolve({bookings, total: response.data.meta.total_count});
    });
  }
  

  viewBooking(bookingId: number){
    const apiUrl = `/b2c2b/api/v1/bookings/${bookingId}`;
    return axiosInstance.get(apiUrl)
    .then(response => {
      const booking:BookingOrder = {
        id: response.data.id,
        editable: response.data.editable,
        booking: {
          id: response.data.id,
          bookingDate: response.data.created_at,
          refNo: response.data.ref_no,
          name: response.data.agent_name,
          bookedBy: response.data.booked_by,
          tourCode: response.data.code,
          pax: response.data.total_quantity,
          destination: response.data.destination,
          packageName: response.data.caption,
          fileUrlCopy: response.data.file_copy_url,
          fileCNUrlCopy: response.data.file_cn_copy_url,
          bookingFormEnabled: response.data.booking_form_enabled,
          status: response.data.payment_status,
          guaranteedIndicator: response.data.guaranteed_indicator,
          paidAmount: response.data.paid_amount,
          adminFee: response.data.admin_fee,
          fare: response.data.selling_price,
          fareDetails: response.data.selling_prices_text,
          paymentDeadline: response.data.final_payment_date,
          salesPerson: response.data.sub_sale_rep,
          customerRemark: response.data.customer_remark,
          balanceAmountDecimal: response.data.balance_amount_decimal,
          payableText: response.data.payable,
        },
        agent: {
          id: response.data.agent?.id,
          name: response.data.agent?.full_name,
          contactNo: response.data.agent?.phone,
          address: response.data.agent?.address,
        },
        flights: response.data.flights?.map(flight => ({
          flightNo: flight.flight_no,
          departure: flight.departure,
          arrival: flight.arrival,
          fromTo: flight.from_to,
          remark: flight.internal_remark,
        })),
        guests: response.data.bookings.map(booking => {
          const insurance_nomination = mapNominationData(booking.insurance_nomination);
          return {
            id: booking.id,
            category: booking.category,
            designation: booking.designation,
            surname: booking.surname,
            given_name: booking.given_name,
            mobile: booking.mobile,
            email: booking.email,
            nationality: booking.nationality,
            nric: booking.nric,
            passport_number: booking.passport_number,
            passport_expiry_date: booking.passport_expiry_date != "" ? dayjs(booking.passport_expiry_date) : "",
            passport_photocopy_image: booking.passport_photocopy_image,
            date_of_birth: booking.date_of_birth != "" ? dayjs(booking.date_of_birth) : "",
            currency_total_price: booking.currency_total_price,
            remark: booking.remark,
            customer_remark: booking.customer_remark,
            over_18: booking.over_18,
            insurance_confirmation: booking.insurance_confirmation,
            policy_number: booking.policy_number,
            insurance_cert: booking.insurance_cert,
            insurance_nomination_flag: booking.insurance_nomination_flag ?? true,
            guardian_id: booking.guardian_id,
            guardian_list: booking.guardian_list,
            ph_designation: booking.ph_designation,
            ph_name: booking.ph_name,
            ph_dob: booking.ph_dob ? dayjs(booking.ph_dob) : null,
            ph_id_number: booking.ph_id_number,
            insurance_nomination,
            witness_surname: booking.witness_surname,
            witness_given_name: booking.witness_given_name,
            witness_passport_number: booking.witness_passport_number,
            infant_bookings_attributes: booking.infant_bookings_attributes.map(infant => {
              const infant_insurance_nomination = mapNominationData(infant.insurance_nomination);
              return {
                id: infant.id,
                surname: infant.surname,
                given_name: infant.given_name,
                date_of_birth: infant.date_of_birth ? dayjs(infant.date_of_birth) : null,
                gender: infant.gender,
                insurance_confirmation: infant.insurance_confirmation,
                policy_number: infant.policy_number,
                insurance_cert: infant.insurance_cert,
                insurance_nomination_flag: infant.insurance_nomination_flag ?? true,
                parent_id: infant.parent_id,
                ph_designation: infant.ph_designation,
                ph_name: infant.ph_name,
                ph_dob: infant.ph_dob ? dayjs(infant.ph_dob) : null,
                ph_id_number: infant.ph_id_number,
                insurance_nomination: infant_insurance_nomination,
                witness_surname: infant.witness_surname,
                witness_given_name: infant.witness_given_name,
                witness_passport_number: infant.witness_passport_number,
              };
            })
          }
        }),
        departureDate: dayjs(response.data.departure).format(departureDateFormat),
        bookingAddress: {
          address_1: response.data.address_1,
          address_2: response.data.address_2,
          state: response.data.state,
          city_name: response.data.city_name,
          country: response.data.country,
          postal_code: response.data.postal_code,
        },
        insuranceAvailable: response.data.insurance_available,
      };
      return Promise.resolve(booking);
    });
  }


  updateBooking(bookingId: number, bookingFormValues){
    const apiUrl = `/b2c2b/api/v1/bookings/${bookingId}`;
    const guests = bookingFormValues.guests.map(guest => {
      if (guest.as_passport_photocopy?.response?.signed_id) {
        guest = { ...guest, as_passport_photocopy: guest.as_passport_photocopy.response.signed_id };
      }
      const {currency_total_price, over_18, guardian_list, passport_photocopy_image, ...guestPayload} = guest;
      return guestPayload;
    });
    const payload = {
      bookings_attributes: guests,
      ...bookingFormValues.bookingAddress,
    }
    return axiosInstance.put(apiUrl, {booking_group: payload})
    .then(response => {
      return Promise.resolve(response.data);
    });
  }

  getBookingInfoByPaymentId(paymentId: number, token: string){
    const apiUrl = `/b2c2b/api/v1/payments/${paymentId}?token=${token}`;
    return axiosInstance.get(apiUrl)
      .then((response) => {
        const paymentInfo = response.data.data.attributes;
        const currency = paymentInfo.amount_currency;
        const serviceFeeCurrency = currencyDisplay(currency, paymentInfo.service_fee);
        const bookings = paymentInfo.booking_group.bookings;
        const finalPaymentDate = !!paymentInfo.booking_group?.final_payment_date ? dayjs(paymentInfo.booking_group?.final_payment_date, "DD/MM/YYYY").format("DD MMM YYYY") : "";
        // `amount` is the pre-fee charge; the card is actually charged amount + service_fee
        // (== customer_total_price). Show the fee-inclusive amount as the paid amount.
        const serviceFeeAmount = Number(paymentInfo.service_fee) || 0;
        const paidAmount = currencyDisplay(currency, Number(paymentInfo.amount) + serviceFeeAmount);
        const grandTotal = currencyDisplay(currency, paymentInfo.booking_group.customer_total_price);
        const balance = Number(paymentInfo.booking_group.balance_amount_decimal);

        let transaction:BookingTransaction = {
          id: paymentInfo.id,
          bookingId: paymentInfo.booking_id,
          bookingFee: paidAmount,
          transactionDate: formatDate(paymentInfo.transaction_date, "HH:mm:ss DD MMM YYYY"),
          paymentMethod: paymentInfo.method,
          status: paymentInfo.result,
          paymentLink: paymentInfo.payment_link,
          serviceFee: serviceFeeCurrency,
          refNo: paymentInfo.order_no,
          paymentStatus: paymentInfo.booking_group.payment_status,
          summary: {
            priceCurrency: currency,
            tourCode: paymentInfo.booking_group.code,
            tourName: paymentInfo.booking_group.caption,
            country: paymentInfo.destination,
            departureDate: `${formatDate(paymentInfo.booking_group.departure_date)} - ${formatDate(paymentInfo.booking_group.arrival_date)}`,          
            fareBreakdown: {
              promo: bookings[0].price_type,
              total : parseCurrencyString(grandTotal),
              items: []
            },
            // Service/admin fee bridges the itemised selling prices to the grand total
            // (items + admin fee == customer_total_price). Only shown when non-zero.
            currencyAdminFee: serviceFeeAmount > 0 ? serviceFeeCurrency : undefined,
            currencyGrandTotal: grandTotal,
            currencyDeposit: paidAmount,
            currencyBalance: currencyDisplay(currency, balance.toFixed(2)),
            finalPaymentDate: finalPaymentDate
          }       
        };

        if(bookings.length > 0){
          // fareBreakdown rows use each pax's all-in selling_price so they sum to
          // customer_total_price (base-only total_price never reconciled).
          const categories = bookings.map(({category, selling_price}) => ({category, price: parseCurrencyString(selling_price)}));
          const infants = bookings.map(({infant_bookings_attributes})=> infant_bookings_attributes).flat();
          const infantCategory = infants.map(infant => ({category: "infant", price: parseCurrencyString(infant.currency_price)}))
          const fareItems = [...categories, ...infantCategory];
          
          transaction.summary.fareBreakdown.items = Object.values(summarizeFare(fareItems));
        }

        return Promise.resolve(transaction);
      });
  }


  retryPayment(bookingId: number, token: string){
    const apiUrl = `/b2c2b/api/v1/bookings/${bookingId}/payments/retry?token=${token}`;
    return axiosInstance.post(apiUrl)
    .then((response) => {
      return Promise.resolve(response.data);
    });
  }

  getPaymentInfo(bookingId: number){
    const apiUrl = `/b2c2b/api/v1/bookings/${bookingId}/new_payment`;
    return axiosInstance.get(apiUrl)
    .then((response) => {
      return Promise.resolve(response.data);
    });
  }

  createPayment(bookingId: number, payload: object){
    const apiUrl = `/b2c2b/api/v1/bookings/${bookingId}/create_payment`;
    return axiosInstance.post(apiUrl, payload)
    .then((response) => {
      return Promise.resolve(response.data);
    });
  }

  getFairs(){
    const apiUrl = `/api/v1/fairs?public=true`;
    return axiosInstance.get(apiUrl)
    .then((response) => {
      const fairs = (response.data?.data ?? []).map(({ attributes: { customer_code } }) => ({ code: customer_code }));
      return fairs;
    });
  }


}

export default new B2C2BApi();
